import { CircleDollarIcon, ClockAltIcon, DepositIcon, SplitPaymentIcon } from "@xola/icons";
import { Alert, ToggleButton, Tooltip } from "@xola/ui-kit";
import { isPartnerPackageProduct } from "common";
import { useField } from "formik";
import { produce } from "immer";
import React, { ReactElement } from "react";
import { Experience, PaymentLineItemStatus, PaymentMethod, PaymentReminderType } from "types";
import shallow from "zustand/shallow";
import { useSeller, useUser } from "../../hooks/useAccount";
import { isCreditCardRequired, showCustomerFields } from "../../models/Experience";
import { isSplitPayEnabled } from "../../models/Seller";
import { isAffiliate } from "../../models/User";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { isGiftPurchaseItem, isMembershipItem, isMerchandiseItem } from "@/models/PurchaseItem";

export const CreatePurchasePaymentWorkflow = React.memo(() => {
    const seller = useSeller();
    const user = useUser();

    const [purchaseAmount, purchaseDueNow, isSplitPayActive] = useCreatePurchaseStore(
        (state) => [state.amount, state.dueNow, state.purchase.splitPayment?.enabled],
        shallow,
    );

    const isGift = useCreatePurchaseStore((state) => isGiftPurchaseItem(state.getActiveItem()));

    const hasMembershipItem = useCreatePurchaseStore((state) =>
        state.purchase.items.some((purchaseItem) => isMembershipItem(purchaseItem)),
    );

    const hasMerchandiseItem = useCreatePurchaseStore((state) =>
        state.purchase.items.some((purchaseItem) => isMerchandiseItem(purchaseItem)),
    );

    const experience = useCreatePurchaseStore((state) => state.getActiveProduct<Experience>());
    const { isPayLater, isPending, hasDeposit } = useCreatePurchaseStore(
        (state) => ({
            isPayLater: state.paymentLineItem.payment.method === PaymentMethod.LATER,
            isPending: state.paymentLineItem.status === PaymentLineItemStatus.PENDING,
            hasDeposit: state.hasDeposit(),
        }),
        shallow,
    );

    const shouldCollectCC = purchaseAmount === 0 && isCreditCardRequired(experience);
    // If there is no payment method, we need to set null because pay later has no payment method

    const { setPurchase, setPaymentMethod } = useCreatePurchaseStore.getState().actions;

    const handleSplitPay = () => {
        setPurchase((draft) => {
            draft.splitPayment.enabled = true;
            draft.paymentReminders = []; // No longer a deposit
            delete draft.payNow;
        });
    };

    const handlePayNow = () => {
        setPurchase((draft) => {
            draft.paymentReminders = []; // No longer a deposit
            draft.splitPayment.enabled = false;
            delete draft.payNow;
        });

        setPaymentMethod(PaymentMethod.CREDIT_CARD);
    };

    const handlePayLater = () => {
        setPurchase((draft) => {
            draft.paymentReminders = []; // No longer a deposit
            draft.splitPayment.enabled = false;
            delete draft.payNow;
        });
        setPaymentMethod(PaymentMethod.LATER);
    };

    const isNowActive = !isPayLater && !hasDeposit;
    const isPartnerExperience = experience?.seller.id !== seller.id;

    const hasDueNow = (purchaseDueNow ?? 0) > 0;
    const isExperienceWithCustomerFields = showCustomerFields(experience) && !hasMerchandiseItem;
    const showSplitPayment =
        isSplitPayEnabled(seller) && hasDueNow && isExperienceWithCustomerFields && !isAffiliate(user);

    const isPayNowWorkflow = isGift || isPartnerExperience || hasMembershipItem || isPartnerPackageProduct(experience);

    return (
        <div className="space-y-1">
            {shouldCollectCC ? (
                <Alert color="primary">
                    <div>A credit card is required to book, but will not be charged</div>
                </Alert>
            ) : (
                <>
                    <div className="text-sm font-bold">Payment Workflow</div>
                    <div
                        className="flex flex-row flex-wrap gap-2"
                        data-testid="create-purchase-payment-workflow-buttons"
                    >
                        {(purchaseDueNow ?? 0) > 0 && (
                            <WorkflowButton
                                icon={<CircleDollarIcon />}
                                name="Now"
                                isActive={isNowActive && !isSplitPayActive}
                                onClick={handlePayNow}
                            />
                        )}

                        {isPayNowWorkflow ? null : (
                            <>
                                {(purchaseDueNow ?? 0) > 0 && !isAffiliate(user) && (
                                    <WorkflowButton
                                        icon={<ClockAltIcon />}
                                        name="Later"
                                        isActive={isPayLater && !isSplitPayActive}
                                        onClick={handlePayLater}
                                    />
                                )}
                                <Tooltip
                                    content="Not available if “Do Not Charge Card Now” is enabled"
                                    disabled={!isPending}
                                >
                                    <DepositWorkflowButton />
                                </Tooltip>

                                {showSplitPayment && (
                                    <WorkflowButton
                                        icon={<SplitPaymentIcon />}
                                        name="Split Pay"
                                        isActive={isSplitPayActive}
                                        onClick={handleSplitPay}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
});

CreatePurchasePaymentWorkflow.displayName = "CreatePurchasePaymentWorkflow";

interface WorkflowButtonProps {
    icon: ReactElement;
    name: string;
    onClick: () => void;
    isActive?: boolean;
    isDisabled?: boolean;
}

const WorkflowButton = ({ icon, name, onClick, isActive = false, isDisabled = false }: WorkflowButtonProps) => {
    const handleClick = () => {
        if (!isActive) {
            onClick();
        }
    };

    return (
        <ToggleButton
            size="medium"
            color={isActive ? "primary" : "secondary"}
            isActive={isActive}
            disabled={isDisabled}
            className={`flex-shrink-0 space-x-1 ${isActive && "!bg-primary-lighter"}`}
            onClick={handleClick}
        >
            {React.cloneElement(icon, { size: "small", className: "relative -top-[0.5px" })}
            <span>{name}</span>
        </ToggleButton>
    );
};

const DepositWorkflowButton = () => {
    const depositDurationHelper = useField("depositDuration");
    const balanceDueOffset = useCreatePurchaseStore((state) => state.getActiveItem().balanceDueOffset ?? 0);

    const [isPending, hasDeposit] = useCreatePurchaseStore(
        (state) => [state.paymentLineItem.status === PaymentLineItemStatus.PENDING, state.hasDeposit()],
        shallow,
    );

    const handleDeposit = () => {
        const { paymentLineItem, amount } = useCreatePurchaseStore.getState();
        const { initializePaymentLineItem } = useCreatePurchaseStore.getState().actions;
        if (paymentLineItem.payment.method !== PaymentMethod.INVOICE) {
            initializePaymentLineItem(amount ?? 0, PaymentMethod.CREDIT_CARD);
        }

        const earliestArrivalDateTime = useCreatePurchaseStore.getState().getEarliestArrivalDateTime();

        useCreatePurchaseStore.setState(
            produce((draft) => {
                draft.paymentLineItem.payment.method = PaymentMethod.CREDIT_CARD;
            }),
        );

        useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
            const dueAt = earliestArrivalDateTime;
            draft.paymentReminders = [
                { type: PaymentReminderType.SELLER_DEPOSIT, dueAt },
                { type: PaymentReminderType.BALANCE_DUE_TRAVELER, dueAt },
            ];
            draft.splitPayment.enabled = false;
        });
        depositDurationHelper[2].setValue(balanceDueOffset);
    };

    return (
        <WorkflowButton
            icon={<DepositIcon />}
            name="Deposit"
            isActive={hasDeposit}
            isDisabled={isPending}
            onClick={handleDeposit}
        />
    );
};
